import React from 'react';

import { Heading, Caption } from '@depop/web-thrift-ui/Typography';
import { FlashBoldIcon } from '@depop/web-ui-kit/Icons/FlashBoldIcon';
import { Tooltip } from '@depop/web-ui-kit/Tooltip';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { Price } from '@/components/Price';
import { ProductCard } from '@/components/ProductCard';
import { SecondaryPriceLine } from '@/components/SecondaryPriceLine';
import { BOOSTED_TEXT_ANY_LOCALE } from '@/constants/boostedListings';
import { type CurrencyCode } from '@/modules/currencies/constants';
import { type DisplayPrice } from '@/modules/price/aliases';
import { isSold } from '@/modules/product/helpers/isSold';
import { type ListProduct, type Preview } from '@/modules/product/types';

export type Props = {
  slug: string;
  preview: Preview;
  price: ListProduct['price'];
  displayPrice?: DisplayPrice;
  showBoostedTag?: boolean;
  status: string;
  sellerUserName?: string;
  handleClick?: () => void;
  HoverOverlay?: React.FC;
  isRearrangeModeActive?: boolean;
};

// If this written inline as part of the Wrapper prop, this will cause re-renders after dragend
function RearrangeableProductCardWrapper({
  children,
}: {
  children: React.ReactNode;
}) {
  return <span>{children}</span>;
}

export function ShopProductCard({
  slug,
  preview,
  showBoostedTag,
  status,
  price,
  displayPrice,
  sellerUserName,
  handleClick,
  HoverOverlay,
  isRearrangeModeActive = false,
}: Props) {
  const t = useTranslations('common');
  const isProductSold = isSold(status);
  const showProductAttributes = !isRearrangeModeActive;

  return (
    <ProductCard
      productHref={slug}
      preview={preview}
      handleClick={handleClick}
      sellerUserName={sellerUserName}
      HoverOverlay={HoverOverlay}
      Wrapper={
        isRearrangeModeActive ? RearrangeableProductCardWrapper : undefined
      }
      ProductAttributes={() =>
        showProductAttributes ? (
          <div className={styles.productAttributesContainer}>
            <div className={styles.priceContainer}>
              {displayPrice ? (
                <>
                  <Price
                    currencyCode={
                      displayPrice.headline_price.currency as CurrencyCode
                    }
                    price={
                      displayPrice.strikethrough_price?.amount ??
                      displayPrice.headline_price.amount
                    }
                    discountedPrice={
                      displayPrice.strikethrough_price
                        ? displayPrice.headline_price.amount
                        : undefined
                    }
                    inPreview
                  />
                  {displayPrice.secondary_price && (
                    <SecondaryPriceLine
                      secondaryPrice={displayPrice.secondary_price}
                    />
                  )}
                </>
              ) : (
                <Price
                  currencyCode={price.currencyName}
                  price={price.priceAmount}
                  discountedPrice={price.discountedPriceAmount}
                  inPreview
                />
              )}
            </div>
          </div>
        ) : null
      }
      Overlay={() => (
        <>
          {isProductSold && (
            <div className={styles.gradientOverlay}>
              <Heading variant="sm" className={styles.soldText} as="p">
                {t('SoldSingular')}
              </Heading>
            </div>
          )}
          {showBoostedTag && showProductAttributes && (
            <Tooltip
              text={t('BoostedListings.BoostedTooltip')}
              maximumWidth={240}
              initialPlacement="bottom"
            >
              <Caption variant="lg-regular" className={styles.boostedTagText}>
                <FlashBoldIcon
                  size={12}
                  desc={BOOSTED_TEXT_ANY_LOCALE}
                  className={styles.boostedTagIcon}
                />
              </Caption>
            </Tooltip>
          )}
        </>
      )}
    />
  );
}
